import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MaterialModule } from '../../../material/material.module';
import { Chapter, FlashCardContent, QuizQuestionContent, SlideItem } from '../../../middle-school-math/models';

export interface ChapterDialogData {
  chapter?: Chapter;
  isNew: boolean;
}

export interface SlideFormItem {
  image: string;
  audio: string;
}

@Component({
  selector: 'app-admin-edit-chapter-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaterialModule, MatSlideToggleModule],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <div class="title-wrap">
          <mat-icon class="header-icon">{{ data.isNew ? 'post_add' : 'edit_note' }}</mat-icon>
          <div>
            <h2 mat-dialog-title class="dialog-title">{{ data.isNew ? 'Add New Chapter' : 'Configure Chapter' }}</h2>
            <p class="dialog-subtitle">{{ chapter.title || 'Untitled Chapter' }} <span *ngIf="chapter.id">({{ chapter.id }})</span></p>
          </div>
        </div>
        <button mat-icon-button (click)="cancel()" aria-label="Close dialog">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-divider></mat-divider>

      <div mat-dialog-content class="dialog-body">
        <mat-tab-group animationDuration="200ms" [(selectedIndex)]="activeTabIndex" (selectedIndexChange)="onTabChange($event)">
          
          <!-- TAB 1: BASIC INFO -->
          <mat-tab>
            <ng-template mat-tab-label>
              <mat-icon class="tab-icon">info</mat-icon>
              <span>General Info</span>
            </ng-template>

            <div class="tab-content">
              <div class="form-grid">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Chapter Title</mat-label>
                  <input matInput [(ngModel)]="chapter.title" (ngModelChange)="onTitleChange()" placeholder="e.g. Basics of Pre-Algebra" required />
                  <mat-hint>The display title shown to students</mat-hint>
                </mat-form-field>

                <div class="field-with-btn">
                  <mat-form-field appearance="outline" class="flex-1">
                    <mat-label>Chapter ID / Slug</mat-label>
                    <input matInput [(ngModel)]="chapter.id" placeholder="e.g. basics_of_prealgebra" required [disabled]="!data.isNew && !allowIdEdit" />
                    <mat-hint>Unique URL-safe identifier</mat-hint>
                  </mat-form-field>
                  <button mat-stroked-button color="primary" type="button" (click)="autoGenerateSlug()" *ngIf="data.isNew || allowIdEdit">
                    <mat-icon>auto_fix_high</mat-icon> Generate
                  </button>
                  <button mat-icon-button type="button" (click)="allowIdEdit = !allowIdEdit" *ngIf="!data.isNew" matTooltip="Unlock ID edit">
                    <mat-icon>{{ allowIdEdit ? 'lock_open' : 'lock' }}</mat-icon>
                  </button>
                </div>

                <div class="field-with-btn">
                  <mat-form-field appearance="outline" class="flex-1">
                    <mat-label>Asset Prefix</mat-label>
                    <input matInput [(ngModel)]="chapter.prefix" placeholder="e.g. basics_of_prealgebra" />
                    <mat-hint>Used for filenames: prefix_slide_0.png, prefix_audio_0.mp3</mat-hint>
                  </mat-form-field>
                  <button mat-stroked-button type="button" (click)="copyIdToPrefix()">
                    <mat-icon>content_copy</mat-icon> From ID
                  </button>
                </div>

                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Quiz Passing Score Percentage (%)</mat-label>
                  <input matInput type="number" min="1" max="100" [(ngModel)]="chapter.quizPassScorePercentage" placeholder="80" required />
                  <mat-hint>Default is 80% passing threshold for quizzes</mat-hint>
                </mat-form-field>

                <!-- FREE PREVIEW TOGGLE CARD -->
                <div class="preview-toggle-card full-width">
                  <div class="preview-toggle-info">
                    <mat-icon class="preview-icon" [style.color]="chapter.isFreePreview ? '#16a34a' : '#64748b'">{{ chapter.isFreePreview ? 'lock_open' : 'lock' }}</mat-icon>
                    <div>
                      <div class="preview-title">Free Preview Access</div>
                      <div class="preview-desc">Allow students to access this chapter's lessons, flashcards, and quizzes without requiring a paid subscription.</div>
                    </div>
                  </div>
                  <mat-slide-toggle [checked]="chapter.isFreePreview" (change)="chapter.isFreePreview = $event.checked" color="primary">
                    {{ chapter.isFreePreview ? 'Free Preview Enabled' : 'Subscriber Only' }}
                  </mat-slide-toggle>
                </div>
              </div>

              <div class="summary-cards">
                <div class="summary-card">
                  <mat-icon color="primary">slideshow</mat-icon>
                  <div class="summary-details">
                    <div class="summary-count">{{ slideItems.length }}</div>
                    <div class="summary-label">Lesson Pages & Audio</div>
                  </div>
                </div>
                <div class="summary-card">
                  <mat-icon style="color: #00897b;">style</mat-icon>
                  <div class="summary-details">
                    <div class="summary-count">{{ chapter.flashCards.length }}</div>
                    <div class="summary-label">Flashcard Pairs</div>
                  </div>
                </div>
                <div class="summary-card">
                  <mat-icon style="color: #e65100;">quiz</mat-icon>
                  <div class="summary-details">
                    <div class="summary-count">{{ chapter.quizQuestions.length }}</div>
                    <div class="summary-label">Quiz Questions</div>
                  </div>
                </div>
              </div>
            </div>
          </mat-tab>

          <!-- TAB 2: PAGES & AUDIO -->
          <mat-tab>
            <ng-template mat-tab-label>
              <mat-icon class="tab-icon">slideshow</mat-icon>
              <span>Pages & Audio ({{ slideItems.length }})</span>
            </ng-template>

            <div class="tab-content">
              <!-- Generator Box -->
              <div class="helper-box">
                <div class="helper-title">
                  <mat-icon>auto_awesome</mat-icon>
                  <span>Batch Page & Audio Sequence Generator</span>
                </div>
                <p class="helper-desc">Quickly generate sequence filenames for pages & audio using prefix <code>{{ chapter.prefix || chapter.id || 'prefix' }}</code></p>
                <div class="helper-controls">
                  <mat-form-field appearance="outline" class="small-field">
                    <mat-label>Start Index</mat-label>
                    <input matInput type="number" [(ngModel)]="slideGenStart" min="0" />
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="small-field">
                    <mat-label>End Index</mat-label>
                    <input matInput type="number" [(ngModel)]="slideGenEnd" min="0" />
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="small-field">
                    <mat-label>Image Ext</mat-label>
                    <input matInput [(ngModel)]="slideGenExt" placeholder="png" />
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="small-field">
                    <mat-label>Audio Ext</mat-label>
                    <input matInput [(ngModel)]="audioGenExt" placeholder="mp3" />
                  </mat-form-field>
                  <button mat-raised-button color="accent" type="button" (click)="generateSlides()">
                    Generate ({{ (slideGenEnd - slideGenStart + 1) > 0 ? (slideGenEnd - slideGenStart + 1) : 0 }} items)
                  </button>
                  <button mat-button color="warn" type="button" (click)="clearSlides()" *ngIf="slideItems.length > 0">
                    Clear All
                  </button>
                </div>
              </div>

              <!-- Pages List -->
              <div class="list-section">
                <div class="list-header">
                  <h3>Page Images & Corresponding Audio</h3>
                  <div class="list-header-actions">
                    <button mat-button color="primary" type="button" (click)="autoDeriveAllAudio()" *ngIf="slideItems.length > 0">
                      <mat-icon>audiotrack</mat-icon> Auto-sync All Audio
                    </button>
                    <button mat-stroked-button color="primary" type="button" (click)="addSlide()">
                      <mat-icon>add</mat-icon> Add Page
                    </button>
                  </div>
                </div>

                <div *ngIf="slideItems.length === 0" class="empty-state">
                  <mat-icon>slideshow</mat-icon>
                  <p>No pages configured yet. Use the Batch Generator above or click "Add Page".</p>
                </div>

                <div class="items-list">
                  <div *ngFor="let slide of slideItems; let i = index" class="slide-pair-row">
                    <div class="pair-header">
                      <span class="index-chip">Page #{{ i }}</span>
                      <div class="item-actions">
                        <button mat-icon-button type="button" (click)="deriveAudioForSlide(i)" matTooltip="Auto-derive audio filename from image">
                          <mat-icon>auto_fix_high</mat-icon>
                        </button>
                        <button mat-icon-button type="button" (click)="moveSlide(i, -1)" [disabled]="i === 0" matTooltip="Move Up">
                          <mat-icon>arrow_upward</mat-icon>
                        </button>
                        <button mat-icon-button type="button" (click)="moveSlide(i, 1)" [disabled]="i === slideItems.length - 1" matTooltip="Move Down">
                          <mat-icon>arrow_downward</mat-icon>
                        </button>
                        <button mat-icon-button color="warn" type="button" (click)="removeSlide(i)" matTooltip="Delete Page">
                          <mat-icon>delete</mat-icon>
                        </button>
                      </div>
                    </div>
                    <div class="pair-inputs">
                      <mat-form-field appearance="outline" class="flex-1">
                        <mat-label>Page Image Filename</mat-label>
                        <mat-icon matPrefix style="margin-right: 6px; color: #1976d2;">image</mat-icon>
                        <input matInput [(ngModel)]="slide.image" (ngModelChange)="onSlideImageChange(i)" placeholder="slide_0.png" />
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="flex-1">
                        <mat-label>Corresponding Audio Filename</mat-label>
                        <mat-icon matPrefix style="margin-right: 6px; color: #00897b;">volume_up</mat-icon>
                        <input matInput [(ngModel)]="slide.audio" placeholder="audio_0.mp3" />
                      </mat-form-field>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </mat-tab>

          <!-- TAB 3: FLASHCARDS -->
          <mat-tab>
            <ng-template mat-tab-label>
              <mat-icon class="tab-icon">style</mat-icon>
              <span>Flashcards ({{ chapter.flashCards.length }})</span>
            </ng-template>

            <div class="tab-content">
              <!-- Generator Box -->
              <div class="helper-box">
                <div class="helper-title">
                  <mat-icon>auto_awesome</mat-icon>
                  <span>Batch Flashcard Generator</span>
                </div>
                <p class="helper-desc">Generate pairs of <code>[prefix]_flash_question_N.png</code> & <code>[prefix]_flash_answer_N.png</code></p>
                <div class="helper-controls">
                  <mat-form-field appearance="outline" class="small-field">
                    <mat-label>Card Count</mat-label>
                    <input matInput type="number" [(ngModel)]="cardGenCount" min="1" />
                  </mat-form-field>
                  <button mat-raised-button color="accent" type="button" (click)="generateFlashCards()">
                    Generate ({{ cardGenCount }} cards)
                  </button>
                  <button mat-button color="warn" type="button" (click)="clearFlashCards()" *ngIf="chapter.flashCards.length > 0">
                    Clear All
                  </button>
                </div>
              </div>

              <!-- Flashcards List -->
              <div class="list-section">
                <div class="list-header">
                  <h3>Flashcard Pairs</h3>
                  <button mat-stroked-button color="primary" type="button" (click)="addFlashCard()">
                    <mat-icon>add</mat-icon> Add Flashcard
                  </button>
                </div>

                <div *ngIf="chapter.flashCards.length === 0" class="empty-state">
                  <mat-icon>style</mat-icon>
                  <p>No flashcards configured yet. Use the Batch Generator above or click "Add Flashcard".</p>
                </div>

                <div class="items-list">
                  <div *ngFor="let card of chapter.flashCards; let i = index" class="card-pair-row">
                    <div class="pair-header">
                      <span class="index-chip">Card #{{ i }}</span>
                      <div class="item-actions">
                        <button mat-icon-button type="button" (click)="moveFlashCard(i, -1)" [disabled]="i === 0" matTooltip="Move Up">
                          <mat-icon>arrow_upward</mat-icon>
                        </button>
                        <button mat-icon-button type="button" (click)="moveFlashCard(i, 1)" [disabled]="i === chapter.flashCards.length - 1" matTooltip="Move Down">
                          <mat-icon>arrow_downward</mat-icon>
                        </button>
                        <button mat-icon-button color="warn" type="button" (click)="removeFlashCard(i)" matTooltip="Delete Card">
                          <mat-icon>delete</mat-icon>
                        </button>
                      </div>
                    </div>
                    <div class="pair-inputs">
                      <mat-form-field appearance="outline" class="flex-1">
                        <mat-label>Question Image Filename</mat-label>
                        <input matInput [(ngModel)]="card.question" placeholder="question_0.png" />
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="flex-1">
                        <mat-label>Answer Image Filename</mat-label>
                        <input matInput [(ngModel)]="card.answer" placeholder="answer_0.png" />
                      </mat-form-field>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </mat-tab>

          <!-- TAB 4: QUIZ QUESTIONS -->
          <mat-tab>
            <ng-template mat-tab-label>
              <mat-icon class="tab-icon">quiz</mat-icon>
              <span>Quiz Questions ({{ chapter.quizQuestions.length }})</span>
            </ng-template>

            <div class="tab-content">
              <!-- Generator Box -->
              <div class="helper-box">
                <div class="helper-title">
                  <mat-icon>auto_awesome</mat-icon>
                  <span>Batch Quiz Generator</span>
                </div>
                <p class="helper-desc">Generate questions with image <code>[prefix]_question_N.png</code></p>
                <div class="helper-controls">
                  <mat-form-field appearance="outline" class="small-field">
                    <mat-label>Questions Count</mat-label>
                    <input matInput type="number" [(ngModel)]="quizGenCount" min="1" />
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="small-field">
                    <mat-label>Default Options</mat-label>
                    <mat-select [(ngModel)]="quizGenOptions">
                      <mat-option [value]="2">2 Options (A, B)</mat-option>
                      <mat-option [value]="3">3 Options (A, B, C)</mat-option>
                      <mat-option [value]="4">4 Options (A, B, C, D)</mat-option>
                      <mat-option [value]="5">5 Options (A, B, C, D, E)</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <button mat-raised-button color="accent" type="button" (click)="generateQuizQuestions()">
                    Generate ({{ quizGenCount }} questions)
                  </button>
                  <button mat-button color="warn" type="button" (click)="clearQuizQuestions()" *ngIf="chapter.quizQuestions.length > 0">
                    Clear All
                  </button>
                </div>
              </div>

              <!-- Quiz List -->
              <div class="list-section">
                <div class="list-header">
                  <h3>Quiz Questions List</h3>
                  <button mat-stroked-button color="primary" type="button" (click)="addQuizQuestion()">
                    <mat-icon>add</mat-icon> Add Question
                  </button>
                </div>

                <div *ngIf="chapter.quizQuestions.length === 0" class="empty-state">
                  <mat-icon>quiz</mat-icon>
                  <p>No quiz questions configured yet. Use the Batch Generator above or click "Add Question".</p>
                </div>

                <div class="items-list">
                  <div *ngFor="let q of chapter.quizQuestions; let i = index" class="quiz-item-row">
                    <div class="pair-header">
                      <span class="index-chip">Q #{{ i }} (ID: {{ q.id }})</span>
                      <div class="item-actions">
                        <button mat-icon-button type="button" (click)="moveQuizQuestion(i, -1)" [disabled]="i === 0" matTooltip="Move Up">
                          <mat-icon>arrow_upward</mat-icon>
                        </button>
                        <button mat-icon-button type="button" (click)="moveQuizQuestion(i, 1)" [disabled]="i === chapter.quizQuestions.length - 1" matTooltip="Move Down">
                          <mat-icon>arrow_downward</mat-icon>
                        </button>
                        <button mat-icon-button color="warn" type="button" (click)="removeQuizQuestion(i)" matTooltip="Delete Question">
                          <mat-icon>delete</mat-icon>
                        </button>
                      </div>
                    </div>
                    <div class="quiz-inputs-grid">
                      <mat-form-field appearance="outline" class="small-field">
                        <mat-label>Question ID</mat-label>
                        <input matInput [(ngModel)]="q.id" placeholder="0" />
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="flex-2">
                        <mat-label>Question Image Filename</mat-label>
                        <input matInput [(ngModel)]="q.image" placeholder="question_0.png" />
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="small-field">
                        <mat-label>Total Options</mat-label>
                        <mat-select [(ngModel)]="q.numberOfOptions">
                          <mat-option [value]="2">2 (A-B)</mat-option>
                          <mat-option [value]="3">3 (A-C)</mat-option>
                          <mat-option [value]="4">4 (A-D)</mat-option>
                          <mat-option [value]="5">5 (A-E)</mat-option>
                        </mat-select>
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="small-field">
                        <mat-label>Correct Answer</mat-label>
                        <mat-select [(ngModel)]="q.correctAnswer">
                          <mat-option value="1">1 (Option A)</mat-option>
                          <mat-option value="2">2 (Option B)</mat-option>
                          <mat-option value="3" *ngIf="q.numberOfOptions >= 3">3 (Option C)</mat-option>
                          <mat-option value="4" *ngIf="q.numberOfOptions >= 4">4 (Option D)</mat-option>
                          <mat-option value="5" *ngIf="q.numberOfOptions >= 5">5 (Option E)</mat-option>
                        </mat-select>
                      </mat-form-field>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </mat-tab>

          <!-- TAB 5: RAW JSON -->
          <mat-tab>
            <ng-template mat-tab-label>
              <mat-icon class="tab-icon">code</mat-icon>
              <span>Raw JSON</span>
            </ng-template>

            <div class="tab-content">
              <div class="json-header">
                <p>View or directly edit the JSON structure for this chapter.</p>
                <div class="json-actions">
                  <button mat-stroked-button color="primary" type="button" (click)="syncFormToJson()">
                    <mat-icon>refresh</mat-icon> Refresh from Form
                  </button>
                  <button mat-raised-button color="accent" type="button" (click)="applyJsonToForm()">
                    <mat-icon>sync</mat-icon> Apply JSON to Form
                  </button>
                </div>
              </div>

              <div *ngIf="jsonError" class="json-error-banner">
                <mat-icon>error</mat-icon>
                <span>{{ jsonError }}</span>
              </div>

              <textarea class="json-editor" [(ngModel)]="rawJsonString" rows="18" spellcheck="false"></textarea>
            </div>
          </mat-tab>

        </mat-tab-group>
      </div>

      <mat-divider></mat-divider>

      <div mat-dialog-actions class="dialog-actions">
        <button mat-button type="button" (click)="cancel()">Cancel</button>
        <button mat-raised-button color="primary" type="button" (click)="save()">
          <mat-icon>save</mat-icon> Save Chapter
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      display: flex;
      flex-direction: column;
      max-height: 90vh;
      width: 100%;
      min-width: 780px;
    }
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.25rem 1.5rem;
      background: #fafafa;
    }
    .title-wrap {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .header-icon {
      font-size: 32px;
      height: 32px;
      width: 32px;
      color: #1976d2;
    }
    .dialog-title {
      margin: 0;
      font-size: 1.35rem;
      font-weight: 600;
      color: #212121;
    }
    .dialog-subtitle {
      margin: 0.2rem 0 0 0;
      font-size: 0.875rem;
      color: #616161;
    }
    .dialog-body {
      padding: 1rem 1.5rem;
      overflow-y: auto;
      max-height: 65vh;
    }
    .tab-icon {
      margin-right: 0.4rem;
      font-size: 20px;
      height: 20px;
      width: 20px;
      vertical-align: middle;
    }
    .tab-content {
      padding: 1.25rem 0;
    }
    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
    }
    .full-width {
      grid-column: 1 / -1;
    }
    .field-with-btn {
      display: flex;
      gap: 0.5rem;
      align-items: flex-start;
    }
    .flex-1 { flex: 1; }
    .flex-2 { flex: 2; }
    .small-field { width: 120px; }

    .summary-cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;
      margin-top: 1.5rem;
    }
    .summary-card {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem 1.25rem;
      background: #f5f7fa;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
    }
    .summary-card mat-icon {
      font-size: 36px;
      height: 36px;
      width: 36px;
    }
    .summary-count {
      font-size: 1.5rem;
      font-weight: 700;
      color: #212121;
    }
    .summary-label {
      font-size: 0.8rem;
      color: #666;
    }

    .helper-box {
      background: #e3f2fd;
      border: 1px solid #90caf9;
      border-radius: 8px;
      padding: 1rem 1.25rem;
      margin-bottom: 1.5rem;
    }
    .helper-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      color: #1565c0;
      margin-bottom: 0.25rem;
    }
    .helper-desc {
      margin: 0 0 0.75rem 0;
      font-size: 0.85rem;
      color: #37474f;
    }
    .helper-controls {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
    }

    .list-section {
      margin-top: 1rem;
    }
    .list-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }
    .list-header-actions {
      display: flex;
      gap: 0.5rem;
      align-items: center;
    }
    .list-header h3 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
    }
    .empty-state {
      text-align: center;
      padding: 2.5rem;
      background: #fafafa;
      border: 2px dashed #e0e0e0;
      border-radius: 8px;
      color: #757575;
    }
    .empty-state mat-icon {
      font-size: 48px;
      height: 48px;
      width: 48px;
      margin-bottom: 0.5rem;
      opacity: 0.5;
    }
    .items-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .slide-pair-row, .card-pair-row, .quiz-item-row {
      background: #fafafa;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      padding: 0.75rem 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    .index-chip {
      background: #e0e0e0;
      font-size: 0.75rem;
      font-weight: 700;
      padding: 0.25rem 0.6rem;
      border-radius: 12px;
      min-width: 40px;
      text-align: center;
    }
    .item-actions {
      display: flex;
      align-items: center;
    }
    .pair-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .pair-inputs {
      display: flex;
      gap: 1rem;
      margin-bottom: -1.25em;
    }
    .quiz-inputs-grid {
      display: flex;
      gap: 0.75rem;
      align-items: center;
      margin-bottom: -1.25em;
    }
    .preview-toggle-card {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 1rem 1.25rem;
      margin-top: 0.25rem;
    }
    .preview-toggle-info {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
    .preview-icon {
      font-size: 26px;
      height: 26px;
      width: 26px;
    }
    .preview-title {
      font-weight: 700;
      font-size: 0.95rem;
      color: #1e293b;
    }
    .preview-desc {
      font-size: 0.8rem;
      color: #64748b;
    }

    .form-row {
      display: flex;
      gap: 0.75rem;
      align-items: center;
      margin-bottom: -1.25em;
    }

    .json-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.75rem;
    }
    .json-actions {
      display: flex;
      gap: 0.5rem;
    }
    .json-editor {
      width: 100%;
      box-sizing: border-box;
      font-family: 'Courier New', Courier, monospace;
      font-size: 0.85rem;
      background: #1e1e1e;
      color: #4ec9b0;
      border: 1px solid #333;
      border-radius: 6px;
      padding: 1rem;
      resize: vertical;
    }
    .json-error-banner {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: #ffebee;
      color: #c62828;
      padding: 0.75rem 1rem;
      border-radius: 4px;
      margin-bottom: 0.75rem;
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      padding: 1rem 1.5rem;
      background: #fafafa;
    }
  `]
})
export class AdminEditChapterDialogComponent implements OnInit {
  chapter: Chapter;
  slideItems: SlideFormItem[] = [];
  allowIdEdit = false;
  activeTabIndex = 0;

  // Slide Generator options
  slideGenStart = 0;
  slideGenEnd = 20;
  slideGenExt = 'png';
  audioGenExt = 'mp3';

  // Card Generator options
  cardGenCount = 10;

  // Quiz Generator options
  quizGenCount = 10;
  quizGenOptions = 4;

  // Raw JSON
  rawJsonString = '';
  jsonError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<AdminEditChapterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChapterDialogData
  ) {
    if (data.chapter) {
      this.chapter = JSON.parse(JSON.stringify(data.chapter));
    } else {
      this.chapter = {
        id: '',
        title: '',
        prefix: '',
        slides: [],
        flashCards: [],
        quizQuestions: [],
        quizPassScorePercentage: '80'
      };
    }

    if (!this.chapter.slides) this.chapter.slides = [];
    if (!this.chapter.flashCards) this.chapter.flashCards = [];
    if (!this.chapter.quizQuestions) this.chapter.quizQuestions = [];
    if (!this.chapter.quizPassScorePercentage) this.chapter.quizPassScorePercentage = '80';
    if (this.chapter.isFreePreview === undefined) this.chapter.isFreePreview = false;

    // Normalize slides into slideItems
    this.initSlideItems();
  }

  ngOnInit() {
    this.syncFormToJson();
  }

  private initSlideItems() {
    this.slideItems = (this.chapter.slides || []).map(s => {
      if (typeof s === 'string') {
        const derived = s
          .replace('_slide_', '_audio_')
          .replace(/\.(png|jpg|jpeg)$/, '.mp3');
        return { image: s, audio: derived };
      } else if (s && typeof s === 'object') {
        const img = (s as any).image || (s as any).imagePath || '';
        const aud = (s as any).audio || (s as any).audioPath || img.replace('_slide_', '_audio_').replace(/\.(png|jpg|jpeg)$/, '.mp3');
        return { image: img, audio: aud };
      }
      return { image: '', audio: '' };
    });
  }

  onTitleChange() {
    if (this.data.isNew && (!this.chapter.id || this.chapter.id.trim() === '')) {
      this.autoGenerateSlug();
    }
  }

  autoGenerateSlug() {
    if (this.chapter.title) {
      this.chapter.id = this.chapter.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      if (!this.chapter.prefix) {
        this.chapter.prefix = this.chapter.id;
      }
    }
  }

  copyIdToPrefix() {
    if (this.chapter.id) {
      this.chapter.prefix = this.chapter.id;
    }
  }

  onTabChange(index: number) {
    if (index === 4) {
      this.syncFormToJson();
    }
  }

  syncFormToJson() {
    this.jsonError = null;
    this.rebuildChapterSlidesFromForm();
    this.rawJsonString = JSON.stringify(this.chapter, null, 2);
  }

  applyJsonToForm() {
    try {
      this.jsonError = null;
      const parsed = JSON.parse(this.rawJsonString);
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('JSON root must be an object');
      }
      this.chapter = parsed;
      if (!this.chapter.slides) this.chapter.slides = [];
      if (!this.chapter.flashCards) this.chapter.flashCards = [];
      if (!this.chapter.quizQuestions) this.chapter.quizQuestions = [];
      this.initSlideItems();
      alert('JSON successfully loaded into form tabs!');
    } catch (e: any) {
      this.jsonError = 'Invalid JSON: ' + e.message;
    }
  }

  // --- Slides Management ---
  addSlide() {
    const nextIdx = this.slideItems.length;
    const prefix = this.chapter.prefix || this.chapter.id || 'slide';
    const imgExt = this.slideGenExt ? this.slideGenExt.replace(/^\./, '') : 'png';
    const audExt = this.audioGenExt ? this.audioGenExt.replace(/^\./, '') : 'mp3';

    this.slideItems.push({
      image: `${prefix}_slide_${nextIdx}.${imgExt}`,
      audio: `${prefix}_audio_${nextIdx}.${audExt}`
    });
  }

  removeSlide(index: number) {
    this.slideItems.splice(index, 1);
  }

  moveSlide(index: number, direction: number) {
    const target = index + direction;
    if (target >= 0 && target < this.slideItems.length) {
      const item = this.slideItems.splice(index, 1)[0];
      this.slideItems.splice(target, 0, item);
    }
  }

  onSlideImageChange(index: number) {
    const slide = this.slideItems[index];
    if (slide && (!slide.audio || slide.audio.trim() === '')) {
      this.deriveAudioForSlide(index);
    }
  }

  deriveAudioForSlide(index: number) {
    const slide = this.slideItems[index];
    if (slide && slide.image) {
      const audExt = this.audioGenExt ? this.audioGenExt.replace(/^\./, '') : 'mp3';
      slide.audio = slide.image
        .replace('_slide_', '_audio_')
        .replace(/\.(png|jpg|jpeg)$/, `.${audExt}`);
    }
  }

  autoDeriveAllAudio() {
    for (let i = 0; i < this.slideItems.length; i++) {
      this.deriveAudioForSlide(i);
    }
  }

  generateSlides() {
    const prefix = this.chapter.prefix || this.chapter.id || 'slide';
    const imgExt = this.slideGenExt ? this.slideGenExt.replace(/^\./, '') : 'png';
    const audExt = this.audioGenExt ? this.audioGenExt.replace(/^\./, '') : 'mp3';

    const generated: SlideFormItem[] = [];
    for (let i = this.slideGenStart; i <= this.slideGenEnd; i++) {
      generated.push({
        image: `${prefix}_slide_${i}.${imgExt}`,
        audio: `${prefix}_audio_${i}.${audExt}`
      });
    }
    this.slideItems = generated;
  }

  clearSlides() {
    if (confirm('Are you sure you want to remove all pages?')) {
      this.slideItems = [];
    }
  }

  // --- Flashcards Management ---
  addFlashCard() {
    const nextIdx = this.chapter.flashCards.length;
    const prefix = this.chapter.prefix || this.chapter.id || 'flash';
    this.chapter.flashCards.push({
      question: `${prefix}_flash_question_${nextIdx}.png`,
      answer: `${prefix}_flash_answer_${nextIdx}.png`
    });
  }

  removeFlashCard(index: number) {
    this.chapter.flashCards.splice(index, 1);
  }

  moveFlashCard(index: number, direction: number) {
    const target = index + direction;
    if (target >= 0 && target < this.chapter.flashCards.length) {
      const item = this.chapter.flashCards.splice(index, 1)[0];
      this.chapter.flashCards.splice(target, 0, item);
    }
  }

  generateFlashCards() {
    const prefix = this.chapter.prefix || this.chapter.id || 'flash';
    const generated: FlashCardContent[] = [];
    for (let i = 0; i < this.cardGenCount; i++) {
      generated.push({
        question: `${prefix}_flash_question_${i}.png`,
        answer: `${prefix}_flash_answer_${i}.png`
      });
    }
    this.chapter.flashCards = generated;
  }

  clearFlashCards() {
    if (confirm('Are you sure you want to remove all flashcards?')) {
      this.chapter.flashCards = [];
    }
  }

  // --- Quiz Questions Management ---
  addQuizQuestion() {
    const nextIdx = this.chapter.quizQuestions.length;
    const prefix = this.chapter.prefix || this.chapter.id || 'quiz';
    this.chapter.quizQuestions.push({
      id: nextIdx.toString(),
      image: `${prefix}_question_${nextIdx}.png`,
      correctAnswer: '1',
      numberOfOptions: 4
    });
  }

  removeQuizQuestion(index: number) {
    this.chapter.quizQuestions.splice(index, 1);
  }

  moveQuizQuestion(index: number, direction: number) {
    const target = index + direction;
    if (target >= 0 && target < this.chapter.quizQuestions.length) {
      const item = this.chapter.quizQuestions.splice(index, 1)[0];
      this.chapter.quizQuestions.splice(target, 0, item);
    }
  }

  generateQuizQuestions() {
    const prefix = this.chapter.prefix || this.chapter.id || 'quiz';
    const generated: QuizQuestionContent[] = [];
    for (let i = 0; i < this.quizGenCount; i++) {
      generated.push({
        id: i.toString(),
        image: `${prefix}_question_${i}.png`,
        correctAnswer: '1',
        numberOfOptions: this.quizGenOptions || 4
      });
    }
    this.chapter.quizQuestions = generated;
  }

  clearQuizQuestions() {
    if (confirm('Are you sure you want to remove all quiz questions?')) {
      this.chapter.quizQuestions = [];
    }
  }

  private rebuildChapterSlidesFromForm() {
    this.chapter.slides = this.slideItems.map(item => {
      const standardAudio = item.image
        .replace('_slide_', '_audio_')
        .replace(/\.(png|jpg|jpeg)$/, '.mp3');
      // If audio matches standard derived name or is empty, we can store string or object
      if (item.audio && item.audio !== standardAudio) {
        return { image: item.image, audio: item.audio };
      }
      return item.image;
    });
  }

  // --- Save / Cancel ---
  save() {
    if (!this.chapter.title || this.chapter.title.trim() === '') {
      alert('Chapter Title is required');
      this.activeTabIndex = 0;
      return;
    }
    if (!this.chapter.id || this.chapter.id.trim() === '') {
      alert('Chapter ID is required');
      this.activeTabIndex = 0;
      return;
    }
    if (this.chapter.quizPassScorePercentage != null && String(this.chapter.quizPassScorePercentage).trim() !== '') {
      this.chapter.quizPassScorePercentage = String(this.chapter.quizPassScorePercentage).trim();
    } else {
      this.chapter.quizPassScorePercentage = '80';
    }

    this.rebuildChapterSlidesFromForm();
    this.dialogRef.close(this.chapter);
  }

  cancel() {
    this.dialogRef.close();
  }
}
